import React, { useEffect } from 'react';
import MediaQuery from 'react-responsive';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';
import cn from 'classnames';
import get from 'lodash/get';

import {
  usePatientAddressFormState,
  PATIENT_ADDRESS_FIELDS,
} from './usePatientAddressForm';
import { DIALOG_TITLE } from '../../../constants/strings';
import {
  EMERGENCY_CONTACT,
  PATIENT_REGISTRATION,
} from '../../../constants/routes';
import { ADD_PATIENT_STEPS } from '../../../constants/props';
import { usePageRedirect } from '../utils/usePageRedirect';
import { getSystemError } from '../utils/convertErrors';
import { BREAKPOINT } from '../../../constants/styles';
import PatientAddressForm from './PatientAddressForm';
import { checkErrors } from '../utils/checkErrors';
import BackButton from '../../common/BackButton';
import ErrorBlock from '../../common/ErrorBlock';
import Loader from '../../common/Loader';
import Button from '../../Button';
import { enhance } from './enhance';
import { useCountrySelect } from '../../../hocs/useCountrySelect';

const PatientRegistrationAddress = ({
  validateData,
  formatMessage,
  countries,
  personalInfo,
  patientAddress,
  patientContacts,
  savePatientData,
  validationError,
  clearValidationData,
  validationPending,
  clearPatientDataByKey,
}) => {
  const {
    loadCountryDetails,
    stateList,
    zipLimit,
    isZipApplicable,
    zipExamples,
  } = useCountrySelect(formatMessage);

  const {
    handleFormChange,
    validateErrors,
    setCountryValue,
    clearErrors,
    formState,
    isValid,
    clearPatientForm,
  } = usePatientAddressFormState(
    { ...patientAddress },
    validationError,
    isZipApplicable,
  );

  const fullCountryName = get(formState, 'country.label', '');
  const fullStateName = get(formState, 'state.label', '');

  useEffect(() => {
    if (
      validationError &&
      !checkErrors(validationError, PATIENT_ADDRESS_FIELDS)
    ) {
      savePatientData({
        [ADD_PATIENT_STEPS.ADDRESS]: {
          ...formState,
          country: fullCountryName,
          state: fullStateName,
        },
      });
      browserHistory.push(`${PATIENT_REGISTRATION}/${EMERGENCY_CONTACT}`);
    }

    return () => clearValidationData();
  }, [validationError]);

  usePageRedirect(PATIENT_REGISTRATION, [patientContacts, personalInfo]);

  const systemError = getSystemError(validationError);

  const handleBack = () => {
    browserHistory.goBack();
  };

  const onCountryChange = data => {
    clearErrors();
    loadCountryDetails(data.value);
    setCountryValue(data);
  };

  const handleClear = () => {
    clearPatientForm();
    clearValidationData();
    clearPatientDataByKey({ key: ADD_PATIENT_STEPS.ADDRESS });
  };

  const handleSubmit = e => {
    e.preventDefault();
    clearErrors();
    validateData({
      ...formState,
      country: fullCountryName,
      state: fullStateName,
    });
  };

  return (
    <div className="Root">
      <div className="g-content" styleName="root">
        <div styleName="header">
          <MediaQuery minWidth={BREAKPOINT}>
            <BackButton onBack={handleBack} />
          </MediaQuery>
        </div>
        <If condition={validationPending}>
          <Loader className="g-centeredAll" />
        </If>
        <div styleName={cn('content', { disabled: validationPending })}>
          <form onSubmit={handleSubmit} styleName="form">
            <label styleName="form--label" className="mb4">
              {formatMessage(DIALOG_TITLE.addPatientTitle)}
            </label>
            <span styleName="form--subtitle">
              {formatMessage({ id: 'profile.section.address' })}
            </span>
            {systemError && <ErrorBlock errors={systemError} className="mt3" />}

            <PatientAddressForm
              {...formState}
              formatMessage={formatMessage}
              validateErrors={validateErrors}
              onChange={handleFormChange}
              countries={countries}
              statesList={stateList}
              zipLimit={zipLimit}
              isZipApplicable={isZipApplicable}
              zipExamples={zipExamples}
              onCountryChange={onCountryChange}
              loadCountryDetails={loadCountryDetails}
            />
            <div styleName="form--btn" className="mt4">
              <Button
                className="mr3"
                type="submit"
                raised
                color="primary"
                disabled={!isValid}
              >
                {formatMessage({ id: 'common.next' })}
              </Button>
              <Button raised color="accent" onClick={handleClear} type="reset">
                {formatMessage({ id: 'common.clear' })}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

PatientRegistrationAddress.propTypes = {
  formatMessage: PropTypes.func,
  validationPending: PropTypes.bool,
  validationError: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  loggedUser: PropTypes.object,
  countries: PropTypes.array,
  statesList: PropTypes.array,
  patientContacts: PropTypes.object,
  patientAddress: PropTypes.object,
  personalInfo: PropTypes.object,
  savePatientData: PropTypes.func,
  validateData: PropTypes.func,
  clearValidationData: PropTypes.func,
  clearPatientDataByKey: PropTypes.func,
};

export default enhance(PatientRegistrationAddress);



// WEBPACK FOOTER //
// ./src/components/PatientRegistration/PatientRegistrationAddress/index.jsx